import type { LevelDef, LevelObject } from '../sim/level/schema';

export type ArenaCheck = {
  kind: 'spawn' | 'overlap' | 'reachability';
  title: string;
  detail: string;
  at?: { x: number; y: number };
};
const LANDINGS = new Set([
  'solid',
  'ice',
  'conveyor',
  'bounce',
  'block.destructible',
  'platform.moving',
  'platform.rotating',
  'platform.disappearing',
  'platform.collapsing',
  'platform.momentum',
]);
const DANGERS = new Set([
  'spikes',
  'lava',
  'saw',
  'laser',
  'spikeball',
  'crusher',
  'barrel.explosive',
]);
export function objectBounds(obj: LevelObject) {
  if (obj.type === 'laser')
    return { left: obj.x, right: obj.x + (obj.w ?? 14), bottom: obj.y - 0.25, top: obj.y + 0.25 };
  const angle = obj.angle ?? 0,
    hw = obj.r ?? (obj.w ?? 2) / 2,
    hh = obj.r ?? (obj.h ?? 1) / 2;
  const ex = obj.r ?? Math.abs(Math.cos(angle)) * hw + Math.abs(Math.sin(angle)) * hh;
  const ey = obj.r ?? Math.abs(Math.sin(angle)) * hw + Math.abs(Math.cos(angle)) * hh;
  return { left: obj.x - ex, right: obj.x + ex, bottom: obj.y - ey, top: obj.y + ey };
}
/** Deliberately conservative editor advice: no simulation, wall jumps, or moving-platform timing. */
export function checkArena(level: LevelDef): ArenaCheck[] {
  const checks: ArenaCheck[] = [],
    b = level.bounds;
  if (level.spawns.length < 4)
    checks.push({
      kind: 'spawn',
      title: 'Four player starts needed',
      detail: `This arena has ${level.spawns.length}. Add flags from Spawns before testing.`,
    });
  const landings = level.objects.flatMap((obj, index) =>
    LANDINGS.has(obj.type) ? [{ obj, index, bounds: objectBounds(obj) }] : [],
  );
  const startsOn = new Set<number>();
  for (const [i, spawn] of level.spawns.entries()) {
    const prefix = `Player start ${i + 1}`;
    if (spawn.x < b.x || spawn.x > b.x + b.w || spawn.y < b.y || spawn.y > b.y + b.h) {
      checks.push({
        kind: 'spawn',
        title: `${prefix} is outside the arena`,
        detail: 'Move this flag inside the arena border.',
        at: spawn,
      });
    }
    const overlap = level.objects.find((obj) => {
      if (!LANDINGS.has(obj.type) && !DANGERS.has(obj.type)) return false;
      const box = objectBounds(obj);
      return (
        spawn.x + 0.35 > box.left &&
        spawn.x - 0.35 < box.right &&
        spawn.y + 0.8 > box.bottom &&
        spawn.y - 0.8 < box.top
      );
    });
    if (overlap)
      checks.push({
        kind: 'spawn',
        title: `${prefix} is too close to a piece`,
        detail: DANGERS.has(overlap.type)
          ? 'A trap overlaps this starting space. Move the flag away before testing.'
          : 'A fighter may begin inside this piece. Leave space around the flag. Rotated pieces use a conservative bounding box.',
        at: spawn,
      });
    const landing = landings
      .filter(
        (p) =>
          spawn.x >= p.bounds.left && spawn.x <= p.bounds.right && p.bounds.top <= spawn.y - 0.6,
      )
      .sort((a, c) => c.bounds.top - a.bounds.top)[0];
    if (landing) startsOn.add(landing.index);
    else
      checks.push({
        kind: 'spawn',
        title: `${prefix} has no clear landing below`,
        detail:
          'Check the first fall in Test. Moving platforms or nearby walls may still make this start playable.',
        at: spawn,
      });
    if (level.spawns.slice(0, i).some((p) => Math.hypot(p.x - spawn.x, p.y - spawn.y) < 1))
      checks.push({
        kind: 'spawn',
        title: `${prefix} overlaps another start`,
        detail: 'Spread the flags apart so fighters have room to arrive.',
        at: spawn,
      });
  }
  let overlaps = 0;
  for (let i = 0; i < landings.length; i++)
    for (let j = i + 1; j < landings.length; j++) {
      const a = landings[i]!,
        c = landings[j]!,
        ba = a.bounds,
        bc = c.bounds;
      const width = Math.min(ba.right, bc.right) - Math.max(ba.left, bc.left),
        height = Math.min(ba.top, bc.top) - Math.max(ba.bottom, bc.bottom);
      const area = Math.min(
        (ba.right - ba.left) * (ba.top - ba.bottom),
        (bc.right - bc.left) * (bc.top - bc.bottom),
      );
      if (width > 0.1 && height > 0.1 && width * height > area * 0.35) {
        overlaps++;
        if (overlaps <= 5)
          checks.push({
            kind: 'overlap',
            title: 'Overlapping terrain',
            detail:
              'These pieces overlap substantially. This can be intentional; check for hidden pieces or unwanted seams. Rotated shapes use bounding boxes.',
            at: { x: (a.obj.x + c.obj.x) / 2, y: (a.obj.y + c.obj.y) / 2 },
          });
      }
    }
  if (overlaps > 5)
    checks.push({
      kind: 'overlap',
      title: `${overlaps - 5} more terrain overlaps`,
      detail:
        'The first five locations are shown above. Overlap advice is not a playability verdict.',
    });
  const reached = new Set(startsOn),
    queue = [...startsOn];
  while (queue.length) {
    const index = queue.shift()!,
      a = landings.find((p) => p.index === index)!;
    for (const c of landings) {
      if (reached.has(c.index)) continue;
      const gap = Math.max(0, c.bounds.left - a.bounds.right, a.bounds.left - c.bounds.right),
        rise = c.bounds.top - a.bounds.top;
      if (gap <= 6 && rise <= 4) {
        reached.add(c.index);
        queue.push(c.index);
      }
    }
  }
  const isolated = landings.filter((p) => !reached.has(p.index) && p.obj.type === 'solid');
  for (const p of isolated.slice(0, 5))
    checks.push({
      kind: 'reachability',
      title: 'Possibly isolated landing',
      detail:
        'Rough jump-distance check: no route from a start using gaps up to 6 and rises up to 4 grid units. Wall jumps, springs and moving platforms are not simulated. Test from here to judge it.',
      at: { x: p.obj.x, y: p.bounds.top + 1 },
    });
  if (isolated.length > 5)
    checks.push({
      kind: 'reachability',
      title: `${isolated.length - 5} more possibly isolated landings`,
      detail: 'This is a geometric estimate. Playtesting is the final check.',
    });
  return checks;
}
